/**
 * CreditPacks store — leaf module (Perf 3.1.b Step 5).
 */
import { create } from 'zustand';
import { creditPacksApi } from '@/lib/api/creditPacks';

const useCreditPacksStore = create((set, get) => ({
  // State
  creditPacks: [], // Array of credit packs
  isLoading: false,
  error: null,
  isFetched: false,

  // Helpers
  addCreditPacks: (newPacks) => {
    if (!Array.isArray(newPacks)) newPacks = [newPacks];

    const existingById = new Map((get().creditPacks || []).map(p => [p.id, p]));

    newPacks.forEach(pack => {
      if (pack && pack.id) {
        existingById.set(pack.id, {
          id: pack.id,
          name: pack.name,
          credits: pack.credits,
          price: pack.price,
          currency: pack.currency,
          orderWeight: pack.orderWeight,
          flags: {
            isDisplayed: pack?.flags?.isDisplayed || false,
            isBestValue: pack?.flags?.isBestValue || false,
            isPopular: pack?.flags?.isPopular || false,
          },
          stats: pack.stats || {},
          pricePerCredit: pack.pricePerCredit,
          createdAt: pack.createdAt,
          updatedAt: pack.updatedAt,
          deletedAt: pack.deletedAt,
        });
      }
    });

    const merged = Array.from(existingById.values()).sort((a, b) => {
      const aw = a.orderWeight ?? 0;
      const bw = b.orderWeight ?? 0;
      if (aw !== bw) return aw - bw;
      return (a.price ?? 0) - (b.price ?? 0);
    });

    set({ creditPacks: merged });
    return merged;
  },

  // Fetch
  fetchCreditPacks: async () => {
    if (get().isFetched || get().isLoading) {
      return get().creditPacks;
    }

    try {
      set({ isLoading: true, error: null });
      const packs = await creditPacksApi.getCreditPacks();
      const merged = get().addCreditPacks(packs);
      set({ isLoading: false, isFetched: true });
      return merged;
    } catch (error) {
      set({ error: error.message || 'Failed to fetch credit packs', isLoading: false, isFetched: true });
      throw error;
    }
  },

  // Selectors
  getCreditPackById: (id) => (get().creditPacks || []).find(p => p.id === id) || null,

  // Reset
  clearCreditPacks: () => set({
    creditPacks: [],
    isLoading: false,
    error: null,
    isFetched: false,
  }),
}));

export { useCreditPacksStore };
export default useCreditPacksStore;
